import React, { useState } from 'react';
import { Heart, ShoppingCart } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { toast } from 'sonner';

const ProductCard = ({ product }) => {
  const { addToCart } = useCart();
  const [selectedSize, setSelectedSize] = useState(product.sizes[0]);
  const [selectedColor, setSelectedColor] = useState(product.colors[0]);

  const handleAddToCart = () => {
    addToCart(product, selectedSize, selectedColor);
    toast.success(`${product.name} added to cart!`);
  };

  return (
    <div
      data-testid={`product-card-${product.id}`}
      className="group product-card bg-[hsl(var(--pure-white))] rounded-3xl overflow-hidden shadow-md hover:shadow-2xl"
    >
      {/* Image container */}
      <div className="relative aspect-square overflow-hidden bg-[hsl(var(--sand))]/30">
        <img
          src={product.image}
          alt={product.name}
          className="product-image w-full h-full object-cover"
        />

        {/* Badges */}
        <div className="absolute top-4 left-4 flex flex-col gap-2">
          {product.isNew && (
            <span className="bg-[hsl(var(--leaf-light))] text-[hsl(var(--earth-green))] px-3 py-1 rounded-full text-xs font-accent font-semibold uppercase">
              New
            </span>
          )}
          {product.biodegradableDays && (
            <span className="bg-[hsl(var(--earth-green))] text-white px-3 py-1 rounded-full text-xs font-accent font-semibold uppercase">
              {product.biodegradableDays} Days
            </span>
          )}
        </div>

        {/* Quick actions */}
        <div className="absolute bottom-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <button className="w-12 h-12 bg-[hsl(var(--pure-white))] rounded-full shadow-lg flex items-center justify-center hover:scale-110 transition-transform">
            <Heart className="w-5 h-5 text-[hsl(var(--earth-green))]" />
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="p-6 space-y-3">
        <div>
          <h3 className="font-heading text-xl text-[hsl(var(--charcoal))] mb-1">{product.name}</h3>
          <p className="font-body text-sm text-[hsl(var(--stone-gray))] line-clamp-2">{product.description}</p>
        </div>

        {/* Size selector */}
        <div>
          <p className="font-accent text-xs text-[hsl(var(--stone-gray))] uppercase mb-2">Size</p>
          <div className="flex gap-2">
            {product.sizes.map(size => (
              <button
                key={size}
                data-testid={`size-${size}-${product.id}`}
                onClick={() => setSelectedSize(size)}
                className={`px-3 py-1 rounded-lg text-sm font-accent font-semibold transition-all ${
                  selectedSize === size
                    ? 'bg-[hsl(var(--earth-green))] text-white'
                    : 'bg-[hsl(var(--sand))] text-[hsl(var(--stone-gray))] hover:bg-[hsl(var(--leaf-light))]'
                }`}
              >
                {size}
              </button>
            ))}
          </div>
        </div>

        {/* Color selector */}
        <div>
          <p className="font-accent text-xs text-[hsl(var(--stone-gray))] uppercase mb-2">Color</p>
          <div className="flex gap-2">
            {product.colors.map(color => (
              <button
                key={color}
                data-testid={`color-${color}-${product.id}`}
                onClick={() => setSelectedColor(color)}
                className={`px-3 py-1 rounded-lg text-sm font-body capitalize transition-all ${
                  selectedColor === color
                    ? 'bg-[hsl(var(--earth-green))] text-white'
                    : 'bg-[hsl(var(--sand))] text-[hsl(var(--stone-gray))] hover:bg-[hsl(var(--leaf-light))]'
                }`}
              >
                {color}
              </button>
            ))}
          </div>
        </div>

        <div className="flex items-center justify-between pt-2">
          <div>
            <span className="font-heading text-2xl text-[hsl(var(--earth-green))]">${product.price}</span>
            {product.comparePrice && (
              <span className="ml-2 text-sm text-[hsl(var(--stone-gray))] line-through">
                ${product.comparePrice}
              </span>
            )}
          </div>

          <button
            data-testid={`add-to-cart-${product.id}`}
            onClick={handleAddToCart}
            disabled={!product.inStock}
            className="btn-primary bg-[hsl(var(--earth-green))] text-[hsl(var(--cream))] px-6 py-3 rounded-xl font-accent font-semibold text-sm uppercase tracking-wider hover:shadow-green disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            <ShoppingCart className="w-4 h-4" />
            Add
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProductCard;